"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CircleCheck, Sparkles, UserRound } from "lucide-react";

const HIGHLIGHT_ICONS = {
  magic: Sparkles,
  person: UserRound,
};

const PricingCard = ({
  plan,
  getPrice,
  accentColor,
  borderColor,
  badgeColor,
  badgeTextColor,
  buttonShadow,
  isTopRow,
  isAnnual,
  popularButtonGradient,
  popularButtonTextColor,
  popularBorderGradient,
  uniformButtons,
}) => {
  const isPopular = plan.popular;
  const useGradientBorder = isPopular && popularBorderGradient;

  const cardContent = (
    <div
      className={`relative flex flex-1 flex-col p-[18px] rounded-[20px] ${
        useGradientBorder
          ? "h-full justify-between"
          : isTopRow
            ? "md:h-[557px] justify-between"
            : "justify-between"
      } ${
        isPopular
          ? "shadow-[0px_12px_14.8px_0px_rgba(10,30,86,0.05)]"
          : "border border-white/10 overflow-hidden"
      }`}
      style={
        isPopular
          ? {
              background: "linear-gradient(180deg, #363636 0%, #1e1e1e 100%)",
              ...(useGradientBorder ? {} : { border: `3px solid ${borderColor}` }),
            }
          : { backgroundColor: "#1e1e1e" }
      }
    >
      {/* MOST POPULAR badge - floats above card */}
      {isPopular && (
        <div
          className="absolute top-[-19px] left-1/2 -translate-x-1/2 px-3 py-2 rounded-lg z-10"
          style={{ backgroundColor: badgeColor }}
        >
          <span
            className="text-sm font-semibold uppercase tracking-[0.56px] whitespace-nowrap"
            style={{ lineHeight: "20px", color: badgeTextColor }}
          >
            MOST POPULAR
          </span>
        </div>
      )}

      {/* Card Content */}
      <div className="flex flex-col gap-6 items-start w-full">
        {/* Plan Info */}
        <div className="flex flex-col gap-2 w-full">
          <h3
            className="text-2xl font-semibold text-[#f8f7f4] tracking-[-0.24px]"
            style={{ lineHeight: "28px" }}
          >
            {plan.name}
          </h3>
          {plan.description && (
            <p
              className="text-base text-[#c4c4c4] tracking-[-0.032px]"
              style={{ lineHeight: "24px" }}
            >
              {plan.description}
            </p>
          )}
          <div className="flex items-end gap-1">
            <span
              className="text-[28px] font-medium text-[#f8f7f4] tracking-[-0.28px]"
              style={{ lineHeight: "36px" }}
            >
              {getPrice(plan)}
            </span>
            <span
              className="text-base text-[#c4c4c4] tracking-[-0.032px]"
              style={{ lineHeight: "24px" }}
            >
              /month
            </span>
          </div>
          {plan.additionalPriceLabel && (
            <p
              className="text-base text-[#f8f7f4] tracking-[-0.032px]"
              style={{ lineHeight: "24px" }}
            >
              {plan.additionalPriceLabel}
            </p>
          )}
          {plan.shopifyPlusPrice && (
            <p
              className="text-sm text-[#909090] tracking-[-0.028px]"
              style={{ lineHeight: "20px" }}
            >
              Shopify Plus: $
              {isAnnual && plan.shopifyPlusAnnualPrice
                ? plan.shopifyPlusAnnualPrice.toFixed(2)
                : plan.shopifyPlusPrice.toFixed(2)}
              /mo
            </p>
          )}
        </div>

        {/* Divider */}
        <div className="h-px w-full bg-white/10" />

        {/* Includes Label */}
        {plan.includesLabel && (
          <p
            className="text-base font-medium text-[#c4c4c4] tracking-[-0.032px] w-full"
            style={{ lineHeight: "24px" }}
          >
            {plan.includesLabel}
          </p>
        )}

        {/* Features */}
        <div className="flex flex-col gap-4 w-full">
          {/* Highlighted service line (e.g. done-for-you setup / dedicated manager) */}
          {plan.highlight && (
            <div className="flex items-start gap-2 w-full">
              {(() => {
                const HighlightIcon =
                  HIGHLIGHT_ICONS[plan.highlight.icon] || Sparkles;
                return (
                  <HighlightIcon
                    className="w-5 h-5 shrink-0"
                    style={{ color: accentColor }}
                    strokeWidth={2}
                  />
                );
              })()}
              <div className="flex flex-col gap-1 flex-1">
                <span
                  className="text-sm font-semibold text-[#f8f7f4] tracking-[-0.028px]"
                  style={{ lineHeight: "20px" }}
                >
                  {plan.highlight.label}
                </span>
                {plan.highlight.sublabel && (
                  <span
                    className="text-[13px] text-[#9a9a9a] tracking-[-0.026px]"
                    style={{ lineHeight: "18px" }}
                  >
                    {plan.highlight.sublabel}
                  </span>
                )}
              </div>
            </div>
          )}

          {plan.features.map((feature, i) => (
            <div key={i} className="flex items-start gap-2 w-full">
              <CircleCheck
                className="w-5 h-5 shrink-0"
                style={{ color: isPopular ? accentColor : "#8b8b8b" }}
                strokeWidth={1.5}
              />
              <span
                className="text-sm text-[#c4c4c4] tracking-[-0.028px] flex-1"
                style={{ lineHeight: "20px" }}
              >
                {feature}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Button */}
      <div className="mt-auto pt-6">
      {isPopular && popularButtonGradient ? (
        /* Popular card: gradient bg, centered */
        <a
          href={plan.ctaLink || "#"}
          className="flex items-center justify-center w-full px-4 py-3 rounded-lg"
          style={{ backgroundImage: popularButtonGradient }}
        >
          <span
            className="text-base font-medium whitespace-nowrap"
            style={{ lineHeight: "24px", color: popularButtonTextColor }}
          >
            {plan.ctaText || "Try for free"}
          </span>
        </a>
      ) : isPopular ? (
        /* Popular card: dark bg, colored border, arrow, outer shadow + hover inset glow */
        <a
          href={plan.ctaLink || "#"}
          className="flex items-center justify-between w-full overflow-hidden px-4 pt-[14px] pb-4 rounded-[10px] bg-[#1e1e1e] transition-shadow duration-500 ease-in-out"
          style={{
            border: `1px solid ${borderColor}`,
            boxShadow: "0px 0px 13.3px 7px #1A1A1A, inset 0px 0px 0px 0px transparent",
            "--hover-shadow": `0px 0px 0px 0px transparent, inset 0px 0px 8.7px 1px ${buttonShadow || "rgba(191,190,184,0.4)"}`,
          }}
          onMouseEnter={(e) => e.currentTarget.style.boxShadow = e.currentTarget.style.getPropertyValue("--hover-shadow")}
          onMouseLeave={(e) => e.currentTarget.style.boxShadow = "0px 0px 13.3px 7px #1A1A1A, inset 0px 0px 0px 0px transparent"}
        >
          <span
            className="text-base font-medium text-[#dbdacd] tracking-[-0.144px] px-2"
            style={{ lineHeight: "20px" }}
          >
            {plan.ctaText || "Try for free"}
          </span>
          <ArrowRight className="w-5 h-5 text-[#dbdacd]" />
        </a>
      ) : (isTopRow && !uniformButtons) ? (
        /* Top row: beige bg, white border, arrow - with hover invert */
        <a
          href={plan.ctaLink || "#"}
          className="group flex items-center justify-between w-full overflow-hidden px-4 pt-[14px] pb-4 rounded-[10px] bg-[#dbdacd] border border-[#f8f7f4] shadow-[inset_0px_0px_0px_0px_transparent] hover:bg-[#1e1e1e] hover:border-[#606060] hover:shadow-[inset_0px_0px_8.7px_1px_#bfbeb8] transition-[background-color,border-color,box-shadow] duration-500 ease-in-out"
        >
          <span
            className="text-base font-medium text-[#3e3e3e] group-hover:text-[#dbdacd] tracking-[-0.144px] px-2 transition-colors duration-500"
            style={{ lineHeight: "20px" }}
          >
            {plan.ctaText || (plan.price === 0 ? "Install free" : "Try for free")}
          </span>
          <ArrowRight className="w-5 h-5 text-[#3e3e3e] group-hover:text-[#dbdacd] transition-colors duration-500" />
        </a>
      ) : (
        /* Secondary button - light bg, gray border, centered, no arrow */
        <a
          href={plan.ctaLink || "#"}
          className="flex items-center justify-center w-full px-4 py-3 rounded-lg bg-[#f8f7f4] border border-[#a4a4a4]"
        >
          <span
            className="text-base font-medium text-[#1e1e1e] px-2"
            style={{ lineHeight: "24px" }}
          >
            {plan.ctaText || "Book a call"}
          </span>
        </a>
      )}
      </div>
    </div>
  );

  if (useGradientBorder) {
    return (
      <div
        className={`relative flex flex-1 rounded-[20px] p-[3px] ${
          isTopRow ? "md:h-[557px]" : ""
        }`}
        style={{ background: popularBorderGradient }}
      >
        {cardContent}
      </div>
    );
  }

  return cardContent;
};

/**
 * Reusable App Pricing Section
 * Responsive layouts:
 * - Desktop (>1100px): 3 cards top row, 2 cards bottom row
 * - Tablet (640px-1100px): 2+2 layout (2 cards per row, shows first 4 cards only)
 * - Mobile (<640px): All cards stacked vertically
 * @param {string} title - Section title
 * @param {Array} plans - Array of pricing plans (5 cards total)
 * @param {string} accentColor - Accent color for check icons on popular card
 * @param {string} popularBorderColor - Border color for popular card
 * @param {string} popularBadgeColor - Badge background color (defaults to accentColor)
 * @param {string} popularBadgeTextColor - Badge text color (defaults to "white")
 * @param {string} popularButtonGlowColor - rgba color for popular button hover inset glow
 */
export const AppPricingSection = ({
  title = "Pricing",
  plans = [],
  accentColor = "#26DBC3",
  popularBorderColor,
  popularBadgeColor,
  popularBadgeTextColor = "white",
  popularButtonGlowColor,
  annualDiscountLabel = "-20%",
  showBillingToggle = true,
  popularButtonGradient,
  popularButtonTextColor = "white",
  popularBorderGradient,
  uniformButtons = false,
  id,
}) => {
  const [isAnnual, setIsAnnual] = useState(false);

  const topRowPlans = plans.slice(0, 3);
  const bottomRowPlans = plans.slice(3);

  const getPrice = (plan) => {
    if (plan.price === 0) return "$0";
    if (isAnnual && plan.annualPrice !== undefined) {
      const p = plan.annualPrice;
      return `$${p % 1 === 0 ? Math.floor(p) : p.toFixed(2)}`;
    }
    const calculated = isAnnual ? (plan.price * 0.8).toFixed(2) : plan.price;
    return `$${calculated % 1 === 0 ? Math.floor(calculated) : calculated}`;
  };

  const borderColor = popularBorderColor || accentColor;
  const badgeColor = popularBadgeColor || accentColor;

  return (
    <section id={id} className="relative z-10 overflow-x-clip bg-[#1e1e1e]">
      <div className="mx-auto px-4 md:px-16 lg:px-32 py-8 md:py-16 max-w-[1440px]">
        <div className="flex flex-col gap-12 items-center max-w-[1184px] mx-auto">
          {/* Heading + Toggle */}
          <div className="flex flex-col gap-12 items-center">
            <h2
              className="text-[32px] md:text-[48px] font-normal text-[#f8f7f4] tracking-[-0.64px] md:tracking-[-0.96px]"
              style={{ lineHeight: "56px" }}
            >
              {title}
            </h2>

            {/* Billing Toggle */}
            {showBillingToggle && <div
              className="relative flex items-center rounded-xl p-1 w-fit"
              style={{
                backgroundColor: "#2e2e2e",
                backdropFilter: "blur(10px)",
                border: "0.5px solid #c0c3d0",
              }}
            >
              <button
                onClick={() => setIsAnnual(false)}
                className="relative z-10 px-4 py-3 rounded-lg text-base font-medium tracking-[0.16px]"
                style={{
                  lineHeight: "24px",
                  color: !isAnnual ? "#383a41" : "#a4a4a4",
                  transition: "color 0.2s ease",
                }}
              >
                {!isAnnual && (
                  <motion.div
                    layoutId="billing-toggle-bg"
                    className="absolute inset-0 bg-white rounded-lg"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10">Monthly billing</span>
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className="relative z-10 flex items-center justify-center gap-2.5 px-4 py-3 rounded-lg text-base font-medium tracking-[0.16px]"
                style={{
                  lineHeight: "24px",
                  color: isAnnual ? "#383a41" : "#a4a4a4",
                  transition: "color 0.2s ease",
                }}
              >
                {isAnnual && (
                  <motion.div
                    layoutId="billing-toggle-bg"
                    className="absolute inset-0 bg-white rounded-lg"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10">Annual billing</span>
                <span
                  className="relative z-10 text-sm tracking-[-0.032px] px-2 pt-1 pb-[5px] rounded-lg hidden md:inline"
                  style={{
                    backgroundColor: isAnnual ? "transparent" : "#1e1e1e",
                    color: isAnnual ? "#383a41" : "#fafafa",
                    lineHeight: "20px",
                    transition: "color 0.2s ease, background-color 0.2s ease",
                  }}
                >
                  {annualDiscountLabel}
                </span>
              </button>
            </div>}
          </div>

          {/* Pricing Cards */}
          <div className="flex flex-col gap-5 w-full">
            {/* Desktop Layout (>1100px): 3 cards top, 2 bottom */}
            {/* Tablet Layout (640px-1100px): 2+2 cards */}
            {/* Mobile Layout (<640px): All cards stacked */}
            
            {/* Desktop (xl+): Original 3+2 layout */}
            <div className="hidden xl:flex flex-col gap-5 w-full">
              {/* Top Row - 3 cards */}
              <div className="flex flex-row gap-6 items-stretch w-full">
                {topRowPlans.map((plan, index) => (
                  <PricingCard
                    key={index}
                    plan={plan}
                    getPrice={getPrice}
                    accentColor={accentColor}
                    borderColor={borderColor}
                    badgeColor={badgeColor}
                    badgeTextColor={popularBadgeTextColor}
                    buttonShadow={popularButtonGlowColor}
                    isTopRow
                    isAnnual={isAnnual}
                    popularButtonGradient={popularButtonGradient}
                    popularButtonTextColor={popularButtonTextColor}
                    popularBorderGradient={popularBorderGradient}
                    uniformButtons={uniformButtons}
                  />
                ))}
              </div>

              {/* Bottom Row - 2 cards */}
              {bottomRowPlans.length > 0 && (
                <div className="flex flex-row gap-5 items-stretch w-full">
                  {bottomRowPlans.map((plan, index) => (
                    <PricingCard
                      key={index}
                      plan={plan}
                      getPrice={getPrice}
                      accentColor={accentColor}
                      borderColor={borderColor}
                      badgeColor={badgeColor}
                      badgeTextColor={popularBadgeTextColor}
                      buttonShadow={popularButtonGlowColor}
                      isTopRow={false}
                      isAnnual={isAnnual}
                      popularButtonGradient={popularButtonGradient}
                      uniformButtons={uniformButtons}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Tablet (sm to xl): 2+2 layout */}
            <div className="hidden sm:flex xl:hidden flex-col gap-5 w-full">
              {/* First Row - 2 cards */}
              <div className="flex flex-row gap-5 items-stretch w-full">
                {plans.slice(0, 2).map((plan, index) => (
                  <PricingCard
                    key={index}
                    plan={plan}
                    getPrice={getPrice}
                    accentColor={accentColor}
                    borderColor={borderColor}
                    badgeColor={badgeColor}
                    badgeTextColor={popularBadgeTextColor}
                    buttonShadow={popularButtonGlowColor}
                    isTopRow
                    isAnnual={isAnnual}
                    popularButtonGradient={popularButtonGradient}
                    popularButtonTextColor={popularButtonTextColor}
                    popularBorderGradient={popularBorderGradient}
                    uniformButtons={uniformButtons}
                  />
                ))}
              </div>

              {/* Second Row - 2 cards */}
              {plans.length > 2 && (
                <div className="flex flex-row gap-5 items-stretch w-full">
                  {plans.slice(2, 4).map((plan, index) => (
                    <PricingCard
                      key={index + 2}
                      plan={plan}
                      getPrice={getPrice}
                      accentColor={accentColor}
                      borderColor={borderColor}
                      badgeColor={badgeColor}
                      badgeTextColor={popularBadgeTextColor}
                      buttonShadow={popularButtonGlowColor}
                      isTopRow
                      isAnnual={isAnnual}
                      popularButtonGradient={popularButtonGradient}
                      popularButtonTextColor={popularButtonTextColor}
                      popularBorderGradient={popularBorderGradient}
                      uniformButtons={uniformButtons}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Mobile (< sm): All cards stacked vertically */}
            <div className="flex sm:hidden flex-col gap-5 w-full">
              {plans.map((plan, index) => (
                <PricingCard
                  key={index}
                  plan={plan}
                  getPrice={getPrice}
                  accentColor={accentColor}
                  borderColor={borderColor}
                  badgeColor={badgeColor}
                  badgeTextColor={popularBadgeTextColor}
                  buttonShadow={popularButtonGlowColor}
                  isTopRow={index < 3}
                  isAnnual={isAnnual}
                  popularButtonGradient={popularButtonGradient}
                  popularButtonTextColor={popularButtonTextColor}
                  popularBorderGradient={popularBorderGradient}
                  uniformButtons={uniformButtons}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
